<template>
  <div
    v-if="visibleUploads.length > 0"
    class="floating-upload-manager"
    :class="{ collapsed }"
  >
    <div
      class="manager-header justify-content-between"
      @click="collapsed = !collapsed"
    >
      <span class="summary"
        >{{ activeCount }} Upload{{ activeCount == 1 ? "" : "s" }} In
        Process</span
      >
      <i class="fa fa-arrow-up ml-2" v-if="collapsed"></i>
      <i class="fa fa-arrow-down ml-2" v-else></i>
    </div>

    <transition-group
      name="fade"
      tag="div"
      class="upload-list"
      v-if="!collapsed"
    >
      <div
        class="upload-item"
        v-for="upload in visibleUploads"
        :key="upload.sessionId"
        :class="{ failed: upload.failed, completed: upload.completed }"
      >
        <div class="file-info">
          <strong class="file-name mr-5 ellipsis">{{
            upload.file.name
          }}</strong>
          <span
            v-if="upload.failed"
            class="status failed cursor-pointer"
            v-tooltip="upload.errorMessage || 'Upload failed'"
            >Failed <i class="fa fa-info-circle ml-1"></i
          ></span>
          <span v-else-if="upload.paused" class="status paused">Paused</span>
          <span v-else-if="upload.completed" class="status uploaded"
            >Completed</span
          >
          <span v-else-if="upload.processing" class="status processing"
            >Processing</span
          >
          <span v-else class="status uploading">Uploading</span>
        </div>

        <div class="progress-bar-container" v-if="!upload.completed">
          <div
            class="progress-bar"
            :style="{
              width: (upload.processing ? 100 : upload.progress) + '%',
            }"
          ></div>
        </div>

        <div class="upload-stats" v-if="!upload.completed">
          <span>{{ upload.processing ? 100 : upload.progress }}%</span>
          <span>{{ formatSpeed(upload.speed) }}</span>
        </div>

        <div class="actions" v-if="!upload.completed">
          <button
            v-if="!upload.paused && !upload.failed"
            @click="pause(upload.sessionId)"
          >
            Pause
          </button>
          <button
            v-if="upload.paused && !upload.failed"
            @click="resume(upload.sessionId)"
          >
            Resume
          </button>
          <button v-if="upload.failed" @click="retry(upload.sessionId)">
            Retry
          </button>
          <button @click="cancel(upload.sessionId)">Cancel</button>
        </div>

        <div class="actions justify-content-end" v-if="upload.completed">
          <button
            class="dismiss-btn d-flex align-items-center justify-content-center"
            @click="dismiss(upload.sessionId)"
          >
            <i class="fa fa-times mr-1"></i> Close
          </button>
        </div>
      </div>
    </transition-group>
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  name: "FloatingUploadManager",
  data() {
    return {
      collapsed: false,
      completeSound: new Audio(require("@/assets/sounds/success_bell.mp3")),
      errorSound: new Audio(require("@/assets/sounds/error_bell.mp3")),
      previousUploads: [],
    };
  },
  computed: {
    ...mapState("uploads", ["uploads"]),
    visibleUploads() {
      return this.uploads;
    },
    activeCount() {
      return this.uploads.filter((u) => !u.completed).length;
    },
  },
  methods: {
    ...mapActions("uploads", [
      "pauseUpload",
      "resumeUpload",
      "cancelUpload",
      "retryUpload",
      "startUpload",
    ]),

    pause(sessionId) {
      this.pauseUpload(sessionId);
    },
    resume(sessionId) {
      this.resumeUpload(sessionId);
      this.startUpload(sessionId);
    },
    cancel(sessionId) {
      this.cancelUpload(sessionId);
    },
    retry(sessionId) {
      this.retryUpload(sessionId);
    },
    dismiss(sessionId) {
      this.cancelUpload(sessionId);
    },
    formatSpeed(speed) {
      if (!speed) return "0 KB/s";
      if (speed < 1024) return speed.toFixed(0) + " B/s";
      else if (speed < 1024 * 1024) return (speed / 1024).toFixed(1) + " KB/s";
      else return (speed / (1024 * 1024)).toFixed(1) + " MB/s";
    },
  },
  watch: {
    uploads: {
      handler(newUploads) {
        this.previousUploads.forEach((oldUpload) => {
          const newUpload = newUploads.find(
            (u) => u.sessionId === oldUpload.sessionId
          );
          if (!newUpload) return;

          if (
            !oldUpload.completed &&
            newUpload.completed &&
            !newUpload.failed
          ) {
            this.completeSound.play();
          }

          if (!oldUpload.failed && newUpload.failed) {
            this.errorSound.play();
          }
        });

        // Deep clone to detach reference
        this.previousUploads = JSON.parse(JSON.stringify(newUploads));
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
.floating-upload-manager {
  position: fixed;
  bottom: 50px;
  right: 20px;
  width: 400px;
  max-width: calc(100% - 40px);
  background-color: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 10px;
  border-radius: 8px;
  z-index: 1000;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.7);
  user-select: none;
  font-size: 14px;

  &.collapsed {
    width: auto;

    .manager-header {
      margin-bottom: 0;
      padding-bottom: 0;
      border-bottom: 0;
    }

    .upload-list {
      display: none;
    }
  }

  .manager-header {
    display: flex;
    align-items: center;
    cursor: pointer;
    margin-bottom: 8px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
    padding-bottom: 8px;
  }

  .summary {
    font-weight: bold;
  }

  .upload-item {
    margin-bottom: 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.15);
    padding-bottom: 8px;

    &.completed {
      opacity: 0.85;
    }
  }

  .file-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
  }

  .status {
    padding: 2px 6px;
    border-radius: 3px;
    white-space: nowrap;

    &.failed {
      background-color: #e74c3c;
    }

    &.paused {
      background-color: #f39c12;
    }

    &.uploading {
      background-color: #27ae60;
    }
    &.uploaded,
    &.processing {
      background-color: #0f763a;
    }
  }

  .progress-bar-container {
    background: #ffffffcc;
    height: 6px;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 4px;

    .progress-bar {
      background-color: #4caf50;
      height: 100%;
      transition: width 0.3s ease;
    }
  }

  .upload-stats {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  .actions {
    display: flex;
    justify-content: space-between;

    &.justify-content-end {
      justify-content: flex-end;
    }

    button {
      background: transparent;
      border: 1px solid #fff;
      border-radius: 4px;
      color: #fff;
      padding: 2px 8px;
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }

      &:focus {
        outline: none;
      }
    }

    .dismiss-btn {
      border-color: rgba(255, 255, 255, 0.4);
      color: rgba(255, 255, 255, 0.7);
      font-size: 13px;
      min-width: 82px;

      &:hover {
        background-color: rgba(255, 255, 255, 0.15);
        color: #fff;
      }
    }
  }

  // Transitions
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
}
</style>
